/**
 * DiscoverUniPanel
 * -----------------------------------------------------------------------------
 * Renders the Discover Uni overlay data as an editorial-tone card: NSS
 * satisfaction, continuation rate, and a legally-required attribution line
 * with Open Government Licence v3 credit.
 *
 * Silently renders nothing when the degree has no DU data (e.g. non-UK
 * institutions). Sample-size + response-rate shown transparently so the
 * student can weigh the confidence of the numbers.
 */
import { ExternalLink } from "lucide-react";

const NSS_LABELS = {
  teaching:  "Teaching",
  feedback:  "Assessment & feedback",
  resources: "Learning resources",
  voice:     "Student voice",
};

export default function DiscoverUniPanel({ degree }) {
  const du = degree?.discover_uni;
  if (!du) return null;
  const nss = degree?.outcomes?.nss_scores;
  const continuation = degree?.outcomes?.continuation_rate;
  const evidence = du.evidence || {};
  const confidence = evidence.confidence || "verified";

  return (
    <div
      id="discover-uni-panel"
      data-testid="discover-uni-panel"
      className="rounded-lg border border-stone-200 bg-white p-5 space-y-4 scroll-mt-20"
    >
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="font-editorial text-lg">Discover Uni — official UK data</h3>
        <span
          data-testid="du-confidence-badge"
          className={
            "text-[10px] font-underground-sc tracking-wider px-2 py-1 rounded " +
            (confidence === "verified"
              ? "bg-emerald-50 text-emerald-700"
              : "bg-amber-50 text-amber-800")
          }
        >
          {confidence}
        </span>
      </div>

      {nss && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Object.entries(NSS_LABELS).map(([k, label]) => (
            nss[k] != null && (
              <div key={k} data-testid={`nss-${k}`}>
                <div className="text-[10px] font-underground-sc text-stone-500">{label}</div>
                <div className="text-2xl font-editorial">{nss[k]}<span className="text-sm text-stone-400">%</span></div>
              </div>
            )
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-stone-600 pt-2 border-t border-stone-100">
        {continuation != null && (
          <span data-testid="du-continuation">
            <strong className="text-stone-800">{Math.round(continuation * 100)}%</strong> continue to Y2
          </span>
        )}
        {nss?.sample_size != null && (
          <span data-testid="du-sample">
            Sample: {nss.sample_size} students ({nss.response_rate}% response)
          </span>
        )}
      </div>

      {/* Legal attribution — required by OGL v3 */}
      <div className="text-[11px] text-stone-500 leading-relaxed pt-3 border-t border-stone-100">
        Data source:{" "}
        <a
          data-testid="du-link"
          href={evidence.url}
          target="_blank"
          rel="noreferrer noopener"
          className="underline hover:text-stone-800 inline-flex items-center gap-1"
        >
          Discover Uni
          <ExternalLink size={11} aria-hidden />
        </a>
        {"  · "}Open Government Licence v3.0 (© HESA / OfS){"  · "}
        checked {evidence.date_checked}.
      </div>
    </div>
  );
}
